<template>
  <main class="maintenance-explained">
    <section class="section explained-hero">
      <div class="shell hero-layout">
        <header class="page-head compact">
          <h1>What the heck is all this?</h1>
          <p>
            Managed maintenance gives your equipment an organized service plan, an ongoing record of care,
            and a clearer picture of what each machine needs next.
          </p>
        </header>
      </div>
    </section>

    <section v-if="scannedMachine" class="section quote-section">
      <div class="shell quote-wrap">
        <div>
          <p class="eyebrow">You scanned a tag</p>
          <h2>{{ scannedHeading }}</h2>
          <p>We already have this machine on record. A monthly plan puts it on a schedule and keeps the record going.</p>
        </div>
        <router-link class="btn btn-primary" :to="quoteTo">Get a quote for this machine</router-link>
      </div>
    </section>

    <section class="section covers-section">
      <div class="shell">
        <header class="page-head compact">
          <h2>What a monthly visit covers</h2>
          <p>A fixed monthly subscription per machine, plus travel. Oil, filters, and repairs are quoted separately as they come up.</p>
        </header>
        <div class="covers-grid">
          <article v-for="item in covered" :key="item.title" class="covers-item">
            <h3>{{ item.title }}</h3>
            <p>{{ item.body }}</p>
          </article>
        </div>
        <p class="covers-note">
          Greasing is billed by zerk count on top of the subscription, because a machine with 40 grease points is not
          the same visit as one with 6. Bring more machines to one stop and the travel share drops for each of them.
        </p>
      </div>
    </section>

    <section class="section benefits-section">
      <div class="shell benefits-wrap">
        <div class="demo-tiles">
          <div class="demo-tiles-head">
            <h2>Try an example machine</h2>
          </div>
          <MachineTiles
            :tiles="demoTiles"
            aria-label="Example machine records"
            link-text="Open example record"
          />
        </div>

        <div class="value-line" aria-label="Managed maintenance benefits">
          <p>Less guesswork.</p>
          <p>Fewer surprises.</p>
          <p>Better records.</p>
        </div>

        <div class="content-grid">
          <article class="benefit benefit-featured">
            <p class="benefit-number">01</p>
            <h2>We carry the maintenance burden</h2>
            <p>
              Instead of relying on memory, scattered notes, or waiting until a machine stops working,
              Centerfire tracks maintenance intervals, completed service, developing concerns, and what should
              be addressed next. The goal is to keep the machine operating while reducing the owner's day-to-day
              maintenance burden.
            </p>
          </article>

          <article class="benefit">
            <p class="benefit-number">02</p>
            <h2>The machine is already giving us information</h2>
            <p>
              Hour meters, developing leaks, inspection findings, fluid condition, and oil samples can reveal a
              problem before it becomes a major failure. When those signals are collected and compared over time,
              a smaller scheduled repair may prevent an untimely breakdown or expensive component replacement.
            </p>
          </article>

          <article class="benefit">
            <p class="benefit-number">03</p>
            <h2>Documented care helps protect value</h2>
            <p>
              Buyers do not simply want to hear that the oil was changed yesterday or that a machine never needed
              repair. They want continuity and proof of care. A regular, organized, transferable record helps show
              how the equipment was treated and gives an owner a firmer grasp of its present condition.
            </p>
          </article>

          <article class="benefit">
            <p class="benefit-number">04</p>
            <h2>A useful record wherever the machine goes</h2>
            <p>
              The same system can bridge trust between a buyer and seller, give an operator confidence about the
              next service interval, and give a technician the history needed to make a better decision. The record
              stays connected to the machine instead of disappearing into a filing cabinet or somebody's memory.
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- Placeholder until the walkthrough videos exist. When they do, each becomes a
         youtube-nocookie.com iframe and nothing else: no tracking scripts on this page. -->
    <section class="section video-section">
      <div class="shell">
        <header class="page-head compact">
          <h2>See it on a real machine</h2>
          <p>Short walkthroughs of a monthly visit and of the record a machine builds up. Coming soon.</p>
        </header>
        <div class="video-grid">
          <div v-for="slot in videoSlots" :key="slot" class="video-slot"><span>{{ slot }}</span></div>
        </div>
      </div>
    </section>

  </main>
</template>

<script>
import MachineTiles from '@/components/MachineTiles.vue';
import { previewMaintainedUnits } from '@/data/maintainedUnitsPreview';
import { loadMachineTagsResult } from '@/data/maintainedUnits';

export default {
  name: 'ManagedMaintenanceExplainedView',
  components: { MachineTiles },
  data() {
    return {
      demoTiles: previewMaintainedUnits.map((u) => ({
        lookupNumber: u.lookupNumber,
        label: u.publicLabel,
        badge: 'Example record',
      })),
      // Aligned with the program copy the invoices and the portal already use.
      covered: [
        { title: 'A scheduled stop', body: 'We come to the machine on a set frequency and work through it in a fixed order, so nothing is skipped because it was not leaking that day.' },
        { title: 'Every grease point', body: 'All of them, counted, every visit — with the zerk count on the invoice so you can see what you paid for.' },
        { title: 'Hours read and recorded', body: 'The meter is read at the machine and written down, which is what makes the next oil change land on time instead of whenever someone remembers.' },
        { title: 'A look for what is starting', body: 'Weeping seals, fluid condition, tire pressure, loose hardware. Small things get flagged while they are still small.' },
        { title: 'Intervals tracked for you', body: 'Oil, filters, and the rest are projected off how the machine is actually being used, not off a calendar guess.' },
        { title: 'A record that stays with it', body: 'Everything done to the machine, on one page you can print or hand to a buyer.' },
      ],
      videoSlots: ['A monthly visit, start to finish', 'What the record looks like after a year'],
      scannedLabel: '',
    };
  },
  computed: {
    // The scanned key selects a machine; it is never echoed into the page.
    scannedMachine() { return String(this.$route.query.machine || '').trim(); },
    scannedHeading() { return this.scannedLabel || 'Put this machine on a plan'; },
    quoteTo() { return { path: '/services-request', query: { machine: this.scannedMachine } }; },
  },
  async created() {
    if (!this.scannedMachine) return;
    const { tags } = await loadMachineTagsResult();
    const machine = tags.find((tag) => tag.lookupNumber && tag.lookupNumber === this.scannedMachine.toUpperCase());
    if (machine?.label) this.scannedLabel = machine.label;
  },
};
</script>

<style scoped>
.quote-wrap { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1.5rem; }
.quote-wrap .eyebrow { margin: 0; color: var(--orange); font-size: .72rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.quote-wrap h2 { margin: .3rem 0; font-weight: 500; }
.covers-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1.5rem; margin-top: 2rem; }
.covers-item h3 { margin: 0 0 .5rem; font-size: 1.05rem; font-weight: 600; }
.covers-note { margin-top: 2rem; opacity: .75; }
.video-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.5rem; margin-top: 2rem; }
.video-slot { display: grid; place-items: center; aspect-ratio: 16 / 9; border: 1px dashed rgba(255, 255, 255, 0.22); border-radius: var(--radius); text-align: center; padding: 1rem; opacity: .6; }

@media (max-width: 900px) { .covers-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 620px) { .covers-grid,.video-grid { grid-template-columns: minmax(0, 1fr); } }

.maintenance-explained {
  min-height: 100vh;
  background:
    radial-gradient(circle at 79% 10%, rgba(255, 90, 2, 0.14), transparent 27%),
    var(--black);
}

.explained-hero {
  padding-top: clamp(10rem, 15vw, 12rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.hero-layout {
  display: grid;
  gap: clamp(2rem, 7vw, 6rem);
  align-items: end;
}

.compact {
  margin: 0;
}

.compact h1 {
  max-width: 13ch;
  font-size: clamp(3rem, 7vw, 6.2rem);
  line-height: 0.94;
  text-transform: uppercase;
}

.compact p {
  max-width: 62ch;
  margin-top: 1rem;
  font-size: clamp(1rem, 1.5vw, 1.18rem);
}

.benefits-section {
  padding-top: clamp(3rem, 6vw, 5rem);
}

.demo-tiles {
  display: grid;
  gap: 1.2rem;
}

.demo-tiles-head h2 {
  font-size: var(--h2);
  font-weight: 400;
}

.demo-tiles-head p {
  margin-top: 0.35rem;
  color: rgba(255, 255, 255, 0.72);
}

.benefits-wrap {
  display: grid;
  gap: clamp(2rem, 4vw, 3rem);
}

.value-line {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid var(--orange);
  border-bottom: 1px solid var(--orange);
}

.value-line p {
  padding: 1rem;
  border-right: 1px solid rgba(255, 255, 255, 0.22);
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.25rem, 2.5vw, 2rem);
  line-height: 1.1;
  text-align: center;
}

.value-line p:nth-child(2) {
  color: var(--orange);
}

.value-line p:last-child {
  border-right: 0;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.benefit {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: clamp(1.3rem, 3vw, 2.2rem);
  border: 1px solid rgba(255, 255, 255, 0.23);
  border-radius: var(--radius);
  background: rgba(0, 0, 0, 0.72);
}

.benefit-featured {
  border-color: rgba(255, 90, 2, 0.75);
  background: linear-gradient(145deg, rgba(255, 90, 2, 0.1), transparent 55%);
}

.benefit-number {
  padding-bottom: 0.8rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--orange);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.78rem;
  letter-spacing: 0.13em;
}

.benefit h2 {
  max-width: 18ch;
  font-size: clamp(1.75rem, 3vw, 2.6rem);
  font-weight: 400;
}

.benefit > p:last-child {
  color: rgba(255, 255, 255, 0.82);
}

@media (max-width: 820px) {
  .hero-layout,
  .content-grid {
    grid-template-columns: 1fr;
  }

  .benefit {
    min-height: 0;
  }
}

@media (max-width: 560px) {
  .value-line {
    grid-template-columns: 1fr;
  }

  .value-line p {
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.22);
  }

  .value-line p:last-child {
    border-bottom: 0;
  }
}
</style>
